import { UserInfo } from "@/types/auth";
import { getFullRoleDisplayName } from "./constants";

// =============================================================================
// ROLE SYSTEM (aligned with database schema)
// =============================================================================

/**
 * Check if user has admin-level role (owner or admin)
 */
export const hasAdminRole = (userInfo?: UserInfo | null): boolean => {
  return userInfo?.role === "owner" || userInfo?.role === "admin";
};

/**
 * Check if user has editor-level role or higher (owner, admin, or editor)
 */
export const hasEditorRole = (userInfo?: UserInfo | null): boolean => {
  return (
    userInfo?.role === "owner" ||
    userInfo?.role === "admin" ||
    userInfo?.role === "editor"
  );
};

/**
 * Check if user is a system-level user (no integrator or organization)
 */
export const isSystemLevel = (userInfo?: UserInfo | null): boolean => {
  return (
    userInfo?.entityType === "system" ||
    (!!userInfo && !userInfo.integratorId && !userInfo.organizationId)
  );
};

/**
 * Check if user is an integrator-level user
 */
export const isIntegratorLevel = (userInfo?: UserInfo | null): boolean => {
  return (
    userInfo?.entityType === "integrator" ||
    (!!userInfo?.integratorId && !userInfo?.organizationId)
  );
};

/**
 * Check if user is an organization-level user
 */
export const isOrganizationLevel = (userInfo?: UserInfo | null): boolean => {
  return userInfo?.entityType === "organization" || !!userInfo?.organizationId;
};

/**
 * Check if user is a system administrator (system level with admin role)
 */
export const isSystemAdmin = (userInfo?: UserInfo | null): boolean => {
  return isSystemLevel(userInfo) && hasAdminRole(userInfo);
};

/**
 * Check if user is an integrator administrator
 */
export const isIntegratorAdmin = (userInfo?: UserInfo | null): boolean => {
  return isIntegratorLevel(userInfo) && hasAdminRole(userInfo);
};

/**
 * Check if user is an organization administrator
 */
export const isOrganizationAdmin = (userInfo?: UserInfo | null): boolean => {
  return isOrganizationLevel(userInfo) && hasAdminRole(userInfo);
};

/**
 * Check if user has tenant-level permissions (integrator admin or system admin)
 * For backward compatibility with existing guards
 */
export const hasTenantPermissions = (userInfo?: UserInfo | null): boolean => {
  return isSystemAdmin(userInfo) || isIntegratorAdmin(userInfo);
};

// =============================================================================
// ROLE-BASED PERMISSION HELPERS
// =============================================================================

/**
 * Check if user has owner role
 */
export const isOwner = (userInfo?: UserInfo | null): boolean => {
  return userInfo?.role === "owner";
};

/**
 * Check if user can create resources (owner, admin, or editor)
 */
export const canCreate = (userInfo?: UserInfo | null): boolean => {
  return hasEditorRole(userInfo);
};

/**
 * Check if user can edit resources (owner, admin, or editor)
 */
export const canEdit = (userInfo?: UserInfo | null): boolean => {
  return hasEditorRole(userInfo);
};

/**
 * Check if user can delete resources (owner or admin only)
 */
export const canDelete = (userInfo?: UserInfo | null): boolean => {
  return hasAdminRole(userInfo);
};

/**
 * Check if user can manage branding/themes (owner only)
 */
export const canManageBranding = (userInfo?: UserInfo | null): boolean => {
  return userInfo?.role === "owner";
};

/**
 * Check if user can manage users (owner or admin)
 */
export const canManageUsers = (userInfo?: UserInfo | null): boolean => {
  return hasAdminRole(userInfo);
};

/**
 * Check if user can assign a specific role
 * - Owners can assign any role
 * - Admins can assign admin, editor, viewer (not owner)
 */
export const canAssignRole = (
  userInfo?: UserInfo | null,
  targetRole?: string
): boolean => {
  if (!userInfo || !targetRole) return false;
  if (userInfo.role === "owner") return true;
  if (userInfo.role === "admin") return targetRole !== "owner";
  return false;
};

/**
 * Check if user can modify a user with the target role
 * - Owners can modify anyone
 * - Admins cannot modify owners
 */
export const canModifyUser = (
  userInfo?: UserInfo | null,
  targetRole?: string
): boolean => {
  if (!userInfo || !targetRole) return false;
  if (userInfo.role === "owner") return true;
  if (userInfo.role === "admin") return targetRole !== "owner";
  return false;
};

/**
 * Check if user can delete a user with the target role
 * - Owners can delete anyone except themselves
 * - Admins can delete editors and viewers only
 */
export const canDeleteUser = (
  userInfo?: UserInfo | null,
  targetRole?: string
): boolean => {
  if (!userInfo || !targetRole) return false;
  if (userInfo.role === "owner") return true;
  if (userInfo.role === "admin") {
    return targetRole === "editor" || targetRole === "viewer";
  }
  return false;
};

/**
 * Get the list of roles the current user can assign
 */
export const getAssignableRoles = (
  userInfo?: UserInfo | null
): string[] => {
  if (!userInfo) return [];
  if (userInfo.role === "owner") {
    return ["owner", "admin", "editor", "viewer"];
  }
  if (userInfo.role === "admin") {
    return ["admin", "editor", "viewer"];
  }
  return [];
};

// =============================================================================
// SCOPE-BASED RESOURCE PERMISSION HELPERS
// =============================================================================

export type ResourceScope = "system" | "integrator" | "organization";

interface ResourceOwnership {
  scope?: ResourceScope;
  integratorId?: string | null;
  organizationId?: string | null;
}

/**
 * Check if user can view a resource based on scope hierarchy
 * - System users can view everything
 * - Integrator users can view system + their integrator + their orgs
 * - Organization users can view system + their integrator + their org
 */
export const canViewResource = (
  userInfo?: UserInfo | null,
  resource?: ResourceOwnership
): boolean => {
  if (!userInfo || !resource) return false;

  // System users can view everything
  if (isSystemLevel(userInfo)) return true;

  const resourceScope = resource.scope || "organization";

  // Everyone can view system-level resources
  if (resourceScope === "system") return true;

  // Integrator users
  if (isIntegratorLevel(userInfo)) {
    if (resourceScope === "integrator") {
      return resource.integratorId === userInfo.integratorId;
    }
    if (resourceScope === "organization") {
      // Can view org resources under their integrator
      return resource.integratorId === userInfo.integratorId;
    }
  }

  // Organization users
  if (isOrganizationLevel(userInfo)) {
    if (resourceScope === "integrator") {
      return resource.integratorId === userInfo.integratorId;
    }
    if (resourceScope === "organization") {
      return resource.organizationId === userInfo.organizationId;
    }
  }

  return false;
};

/**
 * Check if user can edit a resource based on scope + role
 * - Must have editor role or higher
 * - System users can edit everything
 * - Integrator users can edit their integrator + org resources (not system)
 * - Organization users can only edit their org resources
 */
export const canEditResource = (
  userInfo?: UserInfo | null,
  resource?: ResourceOwnership
): boolean => {
  if (!userInfo || !resource) return false;

  // Must have at least editor role
  if (!canEdit(userInfo)) return false;

  // System users can edit everything
  if (isSystemLevel(userInfo)) return true;

  const resourceScope = resource.scope || "organization";

  // No one but system can edit system resources
  if (resourceScope === "system") return false;

  // Integrator users can edit their resources
  if (isIntegratorLevel(userInfo)) {
    return resource.integratorId === userInfo.integratorId;
  }

  // Organization users can only edit their org's resources
  if (isOrganizationLevel(userInfo)) {
    if (resourceScope === "organization") {
      return resource.organizationId === userInfo.organizationId;
    }
    return false;
  }

  return false;
};

/**
 * Check if user can delete a resource based on scope + role
 * - Must have admin role or higher
 * - Same scope rules as edit
 */
export const canDeleteResource = (
  userInfo?: UserInfo | null,
  resource?: ResourceOwnership
): boolean => {
  if (!userInfo || !resource) return false;

  // Must have admin role to delete
  if (!canDelete(userInfo)) return false;

  // System users can delete everything
  if (isSystemLevel(userInfo)) return true;

  const resourceScope = resource.scope || "organization";

  // No one but system can delete system resources
  if (resourceScope === "system") return false;

  // Integrator users can delete their resources
  if (isIntegratorLevel(userInfo)) {
    return resource.integratorId === userInfo.integratorId;
  }

  // Organization users can only delete their org's resources
  if (isOrganizationLevel(userInfo)) {
    if (resourceScope === "organization") {
      return resource.organizationId === userInfo.organizationId;
    }
    return false;
  }

  return false;
};

/**
 * Helper to determine if a resource is owned by the current user's scope
 * Useful for showing "owned by you" vs "inherited" indicators
 */
export const isResourceInUserScope = (
  userInfo?: UserInfo | null,
  resource?: ResourceOwnership
): boolean => {
  if (!userInfo || !resource) return false;

  const resourceScope = resource.scope || "organization";

  if (isSystemLevel(userInfo)) {
    return resourceScope === "system";
  }

  if (isIntegratorLevel(userInfo)) {
    return (
      resourceScope === "integrator" &&
      resource.integratorId === userInfo.integratorId
    );
  }

  if (isOrganizationLevel(userInfo)) {
    return (
      resourceScope === "organization" &&
      resource.organizationId === userInfo.organizationId
    );
  }

  return false;
};

// =============================================================================
// LEGACY COMPATIBILITY (for backward compatibility)
// =============================================================================

/** @deprecated Use isIntegratorAdmin instead */
export const isTenantAdmin = (userInfo?: UserInfo | null): boolean => {
  return isIntegratorAdmin(userInfo);
};

/** @deprecated Use isOrganizationLevel instead */
export const isCustomerUser = (userInfo?: UserInfo | null): boolean => {
  return isOrganizationLevel(userInfo);
};

/** @deprecated Use isSystemAdmin instead */
export const isSysAdmin = (userInfo?: UserInfo | null): boolean => {
  return isSystemAdmin(userInfo);
};

/** @deprecated Use isOrganizationAdmin instead */
export const isOrganizationUser = (userInfo?: UserInfo | null): boolean => {
  return isOrganizationLevel(userInfo);
};

/**
 * Check if a JWT token is expired
 * @param token JWT token string
 * @returns true if token is expired or invalid
 */
export const isTokenExpired = (token?: string | null): boolean => {
  if (!token) return true;

  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // Check if token has expiration time
    if (!payload.exp) return true;

    // Check if current time is past expiration (exp is in seconds, Date.now() is in milliseconds)
    return Date.now() >= payload.exp * 1000;
  } catch {
    // If we can't decode the token, consider it expired
    return true;
  }
};

/**
 * Check if user is authenticated and token is valid
 */
export const isValidAuthentication = (
  user?: { token?: string } | null
): boolean => {
  return !!(user?.token && !isTokenExpired(user.token));
};

/**
 * Check if a JWT token needs refreshing (5 minutes before expiry)
 * @param token JWT token string
 * @returns true if token needs refreshing
 */
export const needsTokenRefresh = (token?: string | null): boolean => {
  if (!token) return true;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return true;

    // Check if token expires within 5 minutes
    const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
    return fiveMinutesFromNow >= payload.exp * 1000;
  } catch {
    return true;
  }
};

/**
 * Get user role display name
 */
export const getUserRoleDisplayName = (userInfo?: UserInfo | null): string => {
  if (!userInfo) return "Unknown";

  if (userInfo.entityType && userInfo.role) {
    return getFullRoleDisplayName(userInfo.entityType, userInfo.role);
  }

  return "Unknown Role";
};
